import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft, CalendarDays, ChartNoAxesCombined, CreditCard, LayoutDashboard, Menu, MonitorPlay, Rows3, SquarePen, Users, X } from "lucide-react";
import { AdminWorkspaceContext } from "../../../components/layout/AdminWorkspaceContext";
import "../styles/admin.css";

const destinations = [
  { to: "/admin", label: "Today", icon: LayoutDashboard },
  { to: "/admin/classes", label: "Classes", icon: CalendarDays },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/programming", label: "Programming", icon: SquarePen },
  { to: "/admin/memberships", label: "Memberships & payments", icon: CreditCard },
  { to: "/admin/reports", label: "Reports", icon: ChartNoAxesCombined },
];

function activeDestination(pathname: string) {
  if (pathname.startsWith("/admin/performance") || pathname === "/admin/insights") return "/admin/reports";
  return destinations.find(({ to }) => to !== "/admin" && (pathname === to || pathname.startsWith(`${to}/`)))?.to ?? pathname;
}

export default function AdminWorkspace() {
  const { pathname, key } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menu = useRef<HTMLElement>(null);
  const menuTrigger = useRef<HTMLButtonElement | null>(null);
  const active = activeDestination(pathname);
  useEffect(() => { setMenuOpen(false); }, [pathname, key]);
  useEffect(() => { if (menuOpen) menu.current?.querySelector<HTMLAnchorElement>("a")?.focus(); }, [menuOpen]);
  const toggleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuTrigger.current = event.currentTarget;
    setMenuOpen((value) => !value);
  };

  const navLink = ({ to, label, icon: Icon }: typeof destinations[number]) => (
    <Link key={to} to={to === "/admin/memberships" ? `${to}/overview` : to}
      aria-current={active === to ? "page" : undefined}
      className={`admin-nav-link${active === to ? " is-active" : ""}`}>
      <Icon size={19} aria-hidden="true" /><span>{label}</span>
    </Link>
  );

  return (
    <AdminWorkspaceContext.Provider value={true}>
      <div className="admin-workspace">
        <a className="admin-skip" href="#admin-content">Skip to content</a>
        <aside className="admin-sidebar" aria-label="Admin workspace">
          <Link className="admin-brand" to="/admin"><img src="/ZERO-ALPHA.png" alt="Zero Alpha" /><span>Admin</span></Link>
          <nav aria-label="Admin">{destinations.map(navLink)}</nav>
          <div className="admin-sidebar-tools">
            {navLink({ to: "/admin/strength-blocks", label: "Strength blocks", icon: Rows3 })}
            <Link className="admin-nav-link" to="/display"><MonitorPlay size={19} aria-hidden="true" />Gym display</Link>
            <Link className="admin-nav-link" to="/dashboard"><ArrowLeft size={19} aria-hidden="true" />Member app</Link>
          </div>
        </aside>
        <div className="admin-body">
          <header className="admin-topbar">
            <Link to="/admin" className="admin-mobile-brand"><img src="/ZERO-ALPHA.png" alt="Zero Alpha" /><span>Admin</span></Link>
            <span className="admin-desktop-label">Zero Alpha Fitness <span>/</span> Admin workspace</span>
            <div className="admin-topbar-actions">
              <Link className="admin-button admin-button-quiet" to="/admin/members?invite=1"><Users size={16} aria-hidden="true" />Invite member</Link>
              <button className="admin-button admin-menu-toggle" type="button" aria-label={menuOpen ? "Close admin menu" : "Open admin menu"}
                aria-expanded={menuOpen} aria-controls="admin-mobile-menu" onClick={toggleMenu}>
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </header>
          {menuOpen && <nav ref={menu} id="admin-mobile-menu" className="admin-mobile-menu" aria-label="More admin destinations"
            onKeyDown={(event) => { if (event.key === "Escape") { setMenuOpen(false); menuTrigger.current?.focus(); } }}>
            {destinations.slice(3).map(navLink)}
            {navLink({ to: "/admin/strength-blocks", label: "Strength blocks", icon: Rows3 })}
            <Link className="admin-nav-link" to="/display"><MonitorPlay size={19} />Gym display</Link>
            <Link className="admin-nav-link" to="/dashboard"><ArrowLeft size={19} />Member app</Link>
          </nav>}
          {active === "/admin/reports" && <nav className="admin-report-tabs" aria-label="Reports"><NavLink to="/admin/insights">Attendance & members</NavLink><NavLink to="/admin/performance" end>Performance</NavLink><NavLink to="/admin/performance/metrics">Metrics</NavLink></nav>}
          {active === "/admin/memberships" && <nav className="admin-report-tabs admin-finance-tabs" aria-label="Memberships and payments"><NavLink to="/admin/memberships/overview">Overview</NavLink><NavLink to="/admin/memberships" end>Memberships</NavLink><NavLink to="/admin/memberships/payments">Payments</NavLink><NavLink to="/admin/memberships/payg">PAYG</NavLink><NavLink to="/admin/memberships/issues">Issues</NavLink></nav>}
          <div id="admin-content" className="admin-content" tabIndex={-1}><React.Suspense fallback={<div className="admin-page" role="status">Loading admin page…</div>}><Outlet /></React.Suspense></div>
        </div>
        <nav className="admin-mobile-tabs" aria-label="Admin shortcuts">
          {destinations.slice(0, 3).map(navLink)}
          <button className={`admin-nav-link${menuOpen || destinations.slice(3).some(({ to }) => to === active) ? " is-active" : ""}`}
            type="button" aria-expanded={menuOpen} aria-controls="admin-mobile-menu" onClick={toggleMenu}><Menu size={19} />More</button>
        </nav>
      </div>
    </AdminWorkspaceContext.Provider>
  );
}
